document.addEventListener('DOMContentLoaded', () => {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  if (typeof gsap !== 'undefined' && !prefersReducedMotion) {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.defaults({
      scroller: window,
    });

    window.addEventListener('lenisReady', () => {
      ScrollTrigger.refresh();
    });

    const isMobile = window.innerWidth <= 767;

    const revealTimeline = gsap.timeline({
      defaults: { ease: 'power2.inOut' },
    });

    document
      .querySelectorAll('.hero, .header, #page-cover')
      .forEach((element) => {
        element.style.animation = 'none';
      });

    revealTimeline
      .to('#page-cover', {
        opacity: 0,
        duration: 2.0,
        onComplete: () => {
          document.querySelector('#page-cover')?.remove();
        },
      })
      .to(
        ['.header', '.hero'],
        {
          opacity: 1,
          duration: 1.4,
        },
        '-=1.4'
      );

    // ============================================================
    // HERO SECTION - Enhanced element reveals
    // ============================================================

    const heroElements = [
      '.section-label',
      '.hero__title',
      '.hero__subtext',
      '.hero__actions',
    ];

    if (isMobile) {
      heroElements.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) {
          gsap.set(element, { opacity: 1, y: 0 });
        }
      });
    } else {
      heroElements.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) {
          gsap.set(element, {
            opacity: 0,
            y: 50,
          });
        }
      });
    }

    gsap.set('.stat-item', { opacity: 0, y: 10, scale: 0.99 });
    gsap.set('.feature-card', { opacity: 0 });

    const collectionHeroContent = document.querySelector(
      '.site-collection-hero__content'
    );
    if (collectionHeroContent) {
      gsap.set(collectionHeroContent, { opacity: 0, y: 40 });
    }

    const articleHeroContent = document.querySelector(
      '.site-article-hero__content'
    );
    if (articleHeroContent) {
      gsap.set(articleHeroContent, { opacity: 0, y: 40 });
    }

    const productGallery = document.querySelector('.site-product__gallery');
    const productInfo = document.querySelector('.site-product__info');
    if (productGallery) gsap.set(productGallery, { opacity: 0, scale: 0.98 });
    if (productInfo) gsap.set(productInfo, { opacity: 0, y: 40 });

    gsap.delayedCall(1.6, () => {
      if (!isMobile) {
        heroElements.forEach((selector, index) => {
          const element = document.querySelector(selector);
          if (element) {
            gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 1.4,
              delay: index * 0.2,
              ease: 'power3.out',
            });
          }
        });
      }

      const statItems = document.querySelectorAll('.stat-item');
      statItems.forEach((stat, index) => {
        gsap.to(stat, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.1,
          delay: 0.8 + index * 0.12,
          ease: 'back.out(1.2)',
        });
      });

      const featureCards = document.querySelectorAll('.feature-card');
      featureCards.forEach((card, index) => {
        gsap.to(card, {
          opacity: 1,
          duration: 1.5,
          delay: 0.9,
          ease: 'power2.out',
        });
      });

      const secondaryAurora = document.querySelector(
        '#secondary-hero-section .hero-aurora'
      );
      if (secondaryAurora) {
        gsap.to(secondaryAurora, {
          opacity: 1,
          duration: 2.0,
          delay: 2.0,
          ease: 'power2.inOut',
        });
      }

      if (collectionHeroContent) {
        gsap.to(collectionHeroContent, {
          opacity: 1,
          y: 0,
          duration: 1.4,
          ease: 'power3.out',
        });
      }

      if (articleHeroContent) {
        gsap.to(articleHeroContent, {
          opacity: 1,
          y: 0,
          duration: 1.4,
          ease: 'power3.out',
        });
      }

      if (productGallery) {
        gsap.to(productGallery, {
          opacity: 1,
          scale: 1,
          duration: 1.4,
          ease: 'power3.out',
        });
      }
      if (productInfo) {
        gsap.to(productInfo, {
          opacity: 1,
          y: 0,
          duration: 1.4,
          delay: 0.2,
          ease: 'power3.out',
        });
      }
    });

    // ============================================================
    // UNIFIED SCROLL REVEAL (all screen sizes)
    // Fades + short-rises each element inside a section, once on
    // enter — so tall sections never animate as one block.
    // ============================================================

    const heroSection = document.querySelector('.hero');

    const revealSelector = [
      '[class*="__header"]',
      '[class*="-header"]',
      '[class*="-card"]',
      '[class*="__item"]',
      '.steps__list',
      '[class*="__content"]',
      '[class*="__left"]',
      '[class*="__right"]',
      '[class*="__media"]',
      '[class*="__image"]',
      '[class*="carousel-wrapper"]',
      '[class*="marquee"]',
      '.btn',
      'input',
      'textarea',
      'select',
    ].join(',');

    document.querySelectorAll('main .shopify-section').forEach((section) => {
      // Hero is already revealed by the intro timeline — skip it.
      if (heroSection && section.contains(heroSection)) return;

      let targets = Array.from(section.querySelectorAll(revealSelector));

      // Keep only the outermost matches (drop targets nested inside
      // another target, e.g. an image inside an already-animated card).
      targets = targets.filter(
        (el) => !targets.some((other) => other !== el && other.contains(el))
      );

      // Fallback: nothing matched → animate the section's first-level blocks.
      if (!targets.length) {
        const inner = section.firstElementChild;
        targets = inner ? Array.from(inner.children) : [];
      }

      targets.forEach((el, i) => {
        // Steps list fades in as one block — no transform/rise.
        const noTransform = el.classList.contains('steps__list');
        gsap.fromTo(
          el,
          noTransform ? { opacity: 0 } : { opacity: 0, y: 20 },
          {
            opacity: 1,
            ...(noTransform ? {} : { y: 0 }),
            duration: 1.1,
            delay: Math.min(i, 6) * 0.12,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 90%',
              toggleActions: 'play none none none',
              once: true,
            },
          }
        );
      });
    });

    ScrollTrigger.refresh();
  }
});
